import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Infrastructure/Store/hooks";
import { Button, Grid, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { fetchMyDepragApi } from "../../../../../Infrastructure/Api/ApiBaseCall";
import { WhatsNewInformation } from "../Overview/Models/WhatsNewInformation";
import { RootState, store } from "../../../../../Infrastructure/Store/store";
import { setAppTitle } from "../../../../../Store/AppSlice";
import { zodResolver } from "@hookform/resolvers/zod";
import { LanguageConsumer, getTranslation } from "../../../../../Infrastructure/Internationalisation/TranslationService";
import { useForm } from "react-hook-form";
import { EmptyWhatsNewRuleForm, WhatsNewRuleForm } from "./Models/WhatsNewRuleForm";
import { WhatsNewEditOrCreateValidator } from "./Models/WhatsNewEditOrCreateValidator";
import { DefaultResponse } from "../../../../../Infrastructure/Api/Model/DefaultResponse";
import { CustomBox } from "../../../../../Layout/CustomBox";
import { LoadingButton } from "@mui/lab";
import { WhatsNewContent } from "./Components/WhatsNewContent";

export const WhatsNewEditOrCreate = () => {
    const { id } = useParams();
    const dispatch = useAppDispatch();
    const location = useLocation();    
    const language = useAppSelector(store => store.settings.language);
    const languageFileWasLoaded = useAppSelector(store => store.languageFile.languageFileWasLoaded);
    const theme = useTheme();
    const navigate = useNavigate();
    const [currentLanguageToEdit, setCurrentLanguageToEdit] = useState<'de' | 'en'>('de');
    const contentDeRef = useRef(null);
    const contentEnRef = useRef(null);
    const contentCnRef = useRef(null); 
    const [contentDe, setContentDe] = useState<string>("");
    const [contentEn, setContentEn] = useState<string>("");
    const [contentCn, setContentCn] = useState<string>("");     


    const [isSaving, setIsSaving] = useState<boolean>(false);

    const { data } = useQuery({
        queryKey: [`emailTriggerRule-${id}`],
        queryFn: async () => await fetchMyDepragApi<WhatsNewInformation>({},
            `highlevel/whatsnew/get/${id}`,
            'GET',
            dispatch,
            store.getState() as RootState),
        enabled: id !== undefined && parseInt(id) > 0
    });

    useEffect(() => {
        if (data) {            
            setContentDe(data.InformationDe)
            setContentEn(data.InformationEn)
            setContentCn(data.InformationCn)              
        }
    }, [data])
  

    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<WhatsNewRuleForm>({
        defaultValues: EmptyWhatsNewRuleForm, values: data ?
            {
                WhatsNewInformationsId: data.WhatsNewInformationsId,                
                WhatsNewHeaderDe: data.HeaderDe,
                WhatsNewHeaderEn: data.HeaderEn,
                WhatsNewHeaderCn: data.HeaderCn                
            } : EmptyWhatsNewRuleForm,
        resolver: zodResolver(WhatsNewEditOrCreateValidator)
    });   

    useEffect(() => {
        dispatch(setAppTitle(getTranslation(id === "0" || id === undefined ? 'MainMenuWhatsNewCreateBreadcrumb' : 'MainMenuWhatsNewEditBreadcrumb')));
    }, [location, language, languageFileWasLoaded, dispatch]);

    const onSubmit = async (data) => {
        setIsSaving(true);
        try {
            let contentDePost = contentDe;
            let contentEnPost = contentEn;
            let contentCnPost = contentCn;        
            
            if (contentDeRef && contentDeRef.current && (contentDeRef.current as any).unprivilegedEditor.getLength() === 1) {
                contentDePost = "";
            }
            if (contentEnRef && contentEnRef.current && (contentEnRef.current as any).unprivilegedEditor.getLength() === 1) {
                contentEnPost = "";
            }
            if (contentCnRef && contentCnRef.current && (contentCnRef.current as any).unprivilegedEditor.getLength() === 1) {
                contentCnPost = "";
            }        

            let dataToPost = {
                WhatsNewInformationsId: id !== undefined && parseInt(id) > 0 ? parseInt(id) : 0,
                HeaderDe: data.WhatsNewHeaderDe,
                HeaderEn: data.WhatsNewHeaderEn,
                HeaderCn: data.WhatsNewHeaderCn,
                InformationDe: contentDePost,
                InformationEn: contentEnPost,
                InformationCn: contentCnPost                         
            } as WhatsNewInformation

            const response = await fetchMyDepragApi<DefaultResponse>(
                dataToPost, 
                'highlevel/whatsnew/edit', 
                'POST', 
                dispatch, 
                store.getState() as RootState, 
                id === "0" || id === undefined ? "SuccessfullyCreatedWhatsNewItem" : "SuccessfullySavedWhatsNewItem");
                navigate(`/highlevel/whatsnew`);
        }catch (error) {
            console.error(error);
          } finally {
            setIsSaving(false);
          }        
    }

    return (
        <LanguageConsumer>
            {({ getTranslatedText }) =>
                <>
                    <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
                        <Grid container direction={'row'} spacing={2} mt={1} className="main-content">
                            <Grid item xs={12}>
                                <Grid container direction={'row'} spacing={2}>
                                    <Grid item xs={12}>
                                        <Button color="darky" sx={{ zIndex: 9999999 }} component={NavLink} to={'/highlevel/whatsnew'} variant="outlined">{getTranslatedText("Prev")}</Button>
                                    </Grid>
                                </Grid>
                            </Grid>                             
                            
                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <CustomBox elevation={0} sx={{ width: '100%', typography: 'body1', border: `1px solid ${theme.palette.cardBorder.main}` }}>
                                    <WhatsNewContent                                      
                                        contentDe={contentDe}
                                        contentEn={contentEn}
                                        contentCn={contentCn}                                        
                                        setContentDe={setContentDe}
                                        setContentEn={setContentEn}
                                        setContentCn={setContentCn}
                                        contentDeRef={contentDeRef}
                                        contentEnRef={contentEnRef}
                                        contentCnRef={contentCnRef}                                        
                                        currentLanguageToEdit={currentLanguageToEdit}
                                        errors={errors}
                                        register={register}
                                        isSaving={isSaving}
                                        setCurrentLanguageToEdit={setCurrentLanguageToEdit}
                                    />
                                </CustomBox>
                            </Grid>

                            <Grid item xs={12} mb={4}>
                                <LoadingButton loading={isSaving} type="submit" variant="contained" fullWidth color="success">
                                    {getTranslatedText("CommonSave")}
                                </LoadingButton>
                            </Grid>

                        </Grid>
                    </form>
                </>
            }
        </LanguageConsumer>
    )
}